import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { MessageCircle, Globe, MapPin, Calendar } from "lucide-react";
import { format, startOfYear, startOfMonth, startOfDay } from "date-fns";
import { ChatSessionsAnalytics } from "./ChatSessionsAnalytics";
import { ChatFeedbackManager } from "./ChatFeedbackManager";

interface ChatSession {
  id: string;
  user_first_name: string;
  user_last_name: string;
  user_location: string | null;
  is_international: boolean;
  region_id: string | null;
  created_at: string;
  regions?: {
    name: string;
  };
}

interface ChatMessage {
  id: string;
  session_id: string;
  message_from: string;
  message_text: string;
  created_at: string;
}

export const ChatSessionsManager = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterYear, setFilterYear] = useState<string>("all");
  const [filterMonth, setFilterMonth] = useState<string>("all");
  const [filterDay, setFilterDay] = useState<string>("all");
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [availableDays, setAvailableDays] = useState<string[]>([]);

  useEffect(() => {
    fetchSessions();
  }, [filterYear, filterMonth, filterDay]);

  useEffect(() => {
    // Extract available years, months, days from sessions
    if (sessions.length > 0) {
      const years = new Set<string>();
      const months = new Set<string>();
      const days = new Set<string>();

      sessions.forEach((session) => {
        const date = new Date(session.created_at);
        years.add(date.getFullYear().toString());
        if (filterYear !== "all" && date.getFullYear().toString() === filterYear) {
          months.add((date.getMonth() + 1).toString());
        }
        if (filterYear !== "all" && filterMonth !== "all" && 
            date.getFullYear().toString() === filterYear && 
            (date.getMonth() + 1).toString() === filterMonth) {
          days.add(date.getDate().toString());
        }
      });

      setAvailableYears(Array.from(years).sort((a, b) => Number(b) - Number(a)));
      setAvailableMonths(Array.from(months).sort((a, b) => Number(a) - Number(b)));
      setAvailableDays(Array.from(days).sort((a, b) => Number(a) - Number(b)));
    }
  }, [sessions, filterYear, filterMonth]);

  const fetchSessions = async () => {
    try {
      let query = supabase
        .from("chat_sessions")
        .select(`
          *,
          regions (
            name
          )
        `)
        .order("created_at", { ascending: false });

      // Apply filters
      if (filterYear !== "all") {
        const yearStart = startOfYear(new Date(Number(filterYear), 0, 1));
        const yearEnd = startOfYear(new Date(Number(filterYear) + 1, 0, 1));
        query = query.gte("created_at", yearStart.toISOString()).lt("created_at", yearEnd.toISOString());
      }

      if (filterMonth !== "all" && filterYear !== "all") {
        const monthStart = startOfMonth(new Date(Number(filterYear), Number(filterMonth) - 1, 1));
        const monthEnd = startOfMonth(new Date(Number(filterYear), Number(filterMonth), 1));
        query = query.gte("created_at", monthStart.toISOString()).lt("created_at", monthEnd.toISOString());
      }

      if (filterDay !== "all" && filterMonth !== "all" && filterYear !== "all") {
        const dayStart = startOfDay(new Date(Number(filterYear), Number(filterMonth) - 1, Number(filterDay)));
        const dayEnd = new Date(dayStart);
        dayEnd.setDate(dayEnd.getDate() + 1);
        query = query.gte("created_at", dayStart.toISOString()).lt("created_at", dayEnd.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error("Error fetching chat sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (sessionId: string) => {
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleViewChat = async (session: ChatSession) => {
    setSelectedSession(session);
    await fetchMessages(session.id);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading chat sessions...</p>
        </CardContent>
      </Card>
    );
  }

  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];

  return (
    <Tabs defaultValue="sessions" className="space-y-4">
      <TabsList>
        <TabsTrigger value="sessions">Chat Sessions</TabsTrigger>
        <TabsTrigger value="analytics">Analytics</TabsTrigger>
        <TabsTrigger value="feedback">Feedback</TabsTrigger>
      </TabsList>

      <TabsContent value="sessions">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Chat Sessions
            </CardTitle>
            <CardDescription>
              View all chatbot conversations from website visitors
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex gap-4 mb-6 items-center flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter by:</span>
              </div>
              
              <Select value={filterYear} onValueChange={(value) => {
                setFilterYear(value);
                setFilterMonth("all");
                setFilterDay("all");
              }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {filterYear !== "all" && (
                <Select value={filterMonth} onValueChange={(value) => {
                  setFilterMonth(value);
                  setFilterDay("all");
                }}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {availableMonths.map((month) => (
                      <SelectItem key={month} value={month}>
                        {monthNames[Number(month) - 1]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {filterMonth !== "all" && filterYear !== "all" && (
                <Select value={filterDay} onValueChange={setFilterDay}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Days</SelectItem>
                    {availableDays.map((day) => (
                      <SelectItem key={day} value={day}>Day {day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {(filterYear !== "all" || filterMonth !== "all" || filterDay !== "all") && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setFilterYear("all");
                    setFilterMonth("all");
                    setFilterDay("all");
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          {sessions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No chat sessions yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell className="font-medium">
                        {session.user_first_name} {session.user_last_name}
                      </TableCell>
                      <TableCell>
                        {session.user_location || "Not provided"}
                      </TableCell>
                      <TableCell>
                        {session.is_international ? (
                          <Badge variant="outline" className="gap-1">
                            <Globe className="w-3 h-3" />
                            International
                          </Badge>
                        ) : session.regions?.name ? (
                          <Badge variant="secondary" className="gap-1">
                            <MapPin className="w-3 h-3" />
                            {session.regions.name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={session.is_international ? "default" : "secondary"}>
                          {session.is_international ? "International" : "USA"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(session.created_at), "MMM d, yyyy h:mm a")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewChat(session)}
                        >
                          View Chat
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="analytics">
        <ChatSessionsAnalytics />
      </TabsContent>

      <TabsContent value="feedback">
        <ChatFeedbackManager />
      </TabsContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Chat with {selectedSession?.user_first_name} {selectedSession?.user_last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Session Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium">Location</p>
                <p className="text-sm text-muted-foreground">
                  {selectedSession?.user_location || "Not provided"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Region</p>
                <p className="text-sm text-muted-foreground">
                  {selectedSession?.is_international
                    ? "International"
                    : selectedSession?.regions?.name || "Not assigned"}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-medium">Date</p>
                <p className="text-sm text-muted-foreground">
                  {selectedSession?.created_at &&
                    format(new Date(selectedSession.created_at), "MMMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="space-y-3">
              <h4 className="font-semibold">Conversation</h4>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`p-3 rounded-lg ${
                    msg.message_from === "bot"
                      ? "bg-primary/10 text-foreground"
                      : "bg-muted text-foreground"
                  }`}
                >
                  <p className="text-xs font-medium mb-1">
                    {msg.message_from === "bot" ? "OFUESI Bot" : `${selectedSession?.user_first_name}`}
                  </p>
                  <p className="text-sm whitespace-pre-line">{msg.message_text}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {format(new Date(msg.created_at), "h:mm a")}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
};
